import React, { Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useOutlet,
} from "react-router-dom";
import { motion, AnimatePresence, Variants } from "framer-motion";
import styled from "styled-components";
import { AnimatedLogo } from "./components/AnimatedLogo";
import { LandingChatwoot } from "./components/LandingChatwoot";
import { LandingReadyProvider } from "./components/LandingReadyContext";
import { AppPageLoader } from "./components/AppPageLoader";
import { ButtonSpinner } from "./components/AnimatedButtonContent";
import { GuestOnlyRoute } from "./components/GuestOnlyRoute";
import { Theme } from "./components/Theme";
import { SessionProvider } from "./components/SessionContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ProfileProvider, useProfile } from "./components/ProfileContext";
import { STANDARD_LAYOUT_SETTINGS } from "./components/motionTransitions";
import { ToastProvider, useToast } from "./components/ToastContext";
import { RouteChunkErrorBoundary } from "./components/RouteChunkErrorBoundary";
import {
  getTelegramWebApp,
  initializeTelegramWebApp,
} from "./components/telegramWebApp";

const loadLandingBundle = () => import("./route-groups/LandingBundle");
const loadAuthBundle = () => import("./route-groups/AuthBundle");
const loadAppBundle = () => import("./route-groups/AppBundle");
const LANDING_LOADER_MIN_DURATION_MS = 1500;

type SuspenseModuleResource<T> = {
  preload: () => Promise<T>;
  read: () => T;
  isResolved: () => boolean;
};

const createSuspenseModuleResource = <T,>(
  loader: () => Promise<T>
): SuspenseModuleResource<T> => {
  let status: "idle" | "pending" | "resolved" | "rejected" = "idle";
  let result: T;
  let error: unknown;
  let promise: Promise<T> | null = null;

  const startLoading = () => {
    if (!promise) {
      status = "pending";
      promise = loader().then(
        (value) => {
          status = "resolved";
          result = value;
          return value;
        },
        (nextError) => {
          status = "rejected";
          error = nextError;
          throw nextError;
        }
      );
    }

    return promise;
  };

  return {
    preload: () => startLoading(),
    isResolved: () => status === "resolved",
    read: () => {
      if (status === "resolved") {
        return result;
      }

      if (status === "rejected") {
        throw error;
      }

      throw startLoading();
    },
  };
};

const authBundleResource = createSuspenseModuleResource(loadAuthBundle);
const appBundleResource = createSuspenseModuleResource(loadAppBundle);

const wait = (durationMs: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, durationMs);
  });

const LandingRoute = lazy(() =>
  Promise.all([
    loadLandingBundle(),
    wait(LANDING_LOADER_MIN_DURATION_MS),
  ]).then(([module]) => ({
    default: module.LandingRoute,
  }))
);

type AuthBundleVariant =
  | "auth"
  | "authtg"
  | "register"
  | "authhapp"
  | "infohapp";

type AppBundleVariant = "main" | "profile" | "pay" | "install";

const pageTransition: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.22,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.18,
      ease: "easeInOut",
    },
  },
};

const AnimatedOutlet: React.FC = () => {
  const location = useLocation();
  const outlet = useOutlet();
  const isLandingRoute = location.pathname === "/";

  return (
    <StyledPageTransitionRoot>
      <AnimatePresence mode="wait" initial={false}>
        <StyledAnimatedPage
          key={location.pathname}
          variants={pageTransition}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <RouteChunkErrorBoundary>
            <Suspense
              fallback={
                isLandingRoute ? (
                  <LandingRouteLoader />
                ) : (
                  <AppPageLoader text="Загрузка данных..." />
                )
              }
            >
              {outlet}
            </Suspense>
          </RouteChunkErrorBoundary>
        </StyledAnimatedPage>
      </AnimatePresence>
    </StyledPageTransitionRoot>
  );
};

const LandingRouteLoader: React.FC = () => {
  return (
    <StyledLandingLoaderWrap
      initial={{ opacity: 0, y: 10, filter: "blur(8px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={STANDARD_LAYOUT_SETTINGS}
    >
      <StyledLandingLoaderLogo>
        <AnimatedLogo height="60px" />
      </StyledLandingLoaderLogo>
    </StyledLandingLoaderWrap>
  );
};

const TelegramBackButtonSync: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const tg = getTelegramWebApp();
    const backButton = tg?.BackButton;

    if (!backButton) {
      return;
    }

    const shouldShowBackButton =
      location.pathname !== "/" &&
      location.pathname !== "/main" &&
      location.pathname !== "/auth";

    const handleBack = () => {
      const historyIndex = window.history.state?.idx;

      if (typeof historyIndex === "number" && historyIndex > 0) {
        navigate(-1);
        return;
      }

      navigate("/main", { replace: true });
    };

    if (shouldShowBackButton) {
      backButton.show?.();
      backButton.onClick?.(handleBack);
    } else {
      backButton.hide?.();
    }

    return () => {
      backButton.offClick?.(handleBack);
    };
  }, [location.pathname, navigate]);

  return null;
};

const ProfileGate: React.FC = () => {
  const { initialLoading, loading, error, errorVersion, refreshProfile } =
    useProfile();
  const { showToast } = useToast();
  const [appBundleReady, setAppBundleReady] = React.useState(() =>
    appBundleResource.isResolved()
  );
  const [appBundleError, setAppBundleError] = React.useState<unknown>(null);

  useEffect(() => {
    let active = true;

    // Load the account pages while the initial profile request is running.
    // Keep the same loader mounted until both are ready.
    void appBundleResource.preload().then(
      () => { if (active) setAppBundleReady(true); },
      (nextError) => { if (active) setAppBundleError(nextError); }
    );

    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!error) return;

    showToast({
      type: "error",
      message: `Ошибка загрузки профиля: ${error}`,
      duration: 3000,
    });
  }, [error, errorVersion, showToast]);

  if (appBundleError !== null) {
    throw appBundleError;
  }

  if (initialLoading || !appBundleReady) {
    return <AppPageLoader text="Загрузка данных..." />;
  }

  if (error) {
    return (
      <StyledProfileErrorWrap
        initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        exit={{ opacity: 0, y: -8, filter: "blur(6px)" }}
        transition={{ duration: 0.28, ease: "easeOut" }}
      >
        <StyledProfileErrorCard>
          <StyledProfileErrorTitle>
            Не удалось загрузить данные
          </StyledProfileErrorTitle>

          <StyledProfileErrorText>
            Проверьте подключение к интернету и попробуйте выполнить загрузку
            повторно.
          </StyledProfileErrorText>

          <StyledRetryButton
            onClick={() =>
              refreshProfile({
                silent: false,
                preserveErrorView: true,
              })
            }
            disabled={loading}
            type="button"
          >
            <AnimatePresence mode="wait" initial={false}>
              {loading ? (
                <StyledRetryContent
                  key="loading"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18 }}
                >
                  <ButtonSpinner size={18} />
                </StyledRetryContent>
              ) : (
                <StyledRetryContent
                  key="default"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18 }}
                >
                  <span>Обновить</span>
                </StyledRetryContent>
              )}
            </AnimatePresence>
          </StyledRetryButton>
        </StyledProfileErrorCard>
      </StyledProfileErrorWrap>
    );
  }

  return <Outlet />;
};

const AuthBundlePage: React.FC<{ variant: AuthBundleVariant }> = ({
  variant,
}) => {
  const module = authBundleResource.read();

  if (variant === "auth") {
    return <module.AuthRoute />;
  }

  if (variant === "authtg") {
    return <module.AuthTgRoute />;
  }

  if (variant === "register") {
    return <module.RegisterRoute />;
  }

  if (variant === "authhapp") {
    return <module.AuthHappRoute />;
  }

  return <module.InfoHappRoute />;
};

const AppBundlePage: React.FC<{ variant: AppBundleVariant }> = ({
  variant,
}) => {
  const module = appBundleResource.read();

  if (variant === "main") {
    return <module.MainRoute />;
  }

  if (variant === "profile") {
    return <module.ProfileRoute />;
  }

  if (variant === "pay") {
    return <module.PayRoute />;
  }

  return <module.InstallRoute />;
};

const AppRoutes: React.FC = () => {
  return (
      <Routes>
      <Route element={<AnimatedOutlet />}>
      <Route path="/" element={<LandingRoute />} />
      <Route element={<GuestOnlyRoute />}>
        <Route path="/auth" element={<AuthBundlePage variant="auth" />} />
        <Route path="/authtg" element={<AuthBundlePage variant="authtg" />} />
      </Route>
      <Route path="/authhapp" element={<AuthBundlePage variant="authhapp" />} />
      <Route path="/infohapp" element={<AuthBundlePage variant="infohapp" />} />
      <Route path="/register" element={<AuthBundlePage variant="register" />} />

        <Route element={<ProtectedRoute />}>
        <Route element={<ProfileGate />}>
          <Route path="/main" element={<AppBundlePage variant="main" />} />
          <Route path="/profile" element={<AppBundlePage variant="profile" />} />
          <Route path="/pay" element={<AppBundlePage variant="pay" />} />
          <Route path="/install" element={<AppBundlePage variant="install" />} />
        </Route>
      </Route>

      <Route
        path="*"
        element={<StyledNotFound>404 - Страница не найдена</StyledNotFound>}
      />
      </Route>
      </Routes>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    initializeTelegramWebApp({
      headerColor: Theme.colors.pageBgColor,
    });
  }, []);

  return (
    <StyledAppRoot
      as={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.24 }}
    >
      <ToastProvider>
        <SessionProvider>
          <ProfileProvider>
            <BrowserRouter basename="/">
              <LandingReadyProvider>
                <LandingChatwoot />
                <TelegramBackButtonSync />
                <AppRoutes />
              </LandingReadyProvider>
            </BrowserRouter>
          </ProfileProvider>
        </SessionProvider>
      </ToastProvider>
    </StyledAppRoot>
  );
};

export default App;

const StyledPageTransitionRoot = styled.div`
  position: relative;
  width: 100%;
  min-height: 100dvh;

  @supports not (height: 100dvh) {
    min-height: 100vh;
  }
`;

const StyledAnimatedPage = styled(motion.div)`
  width: 100%;
  min-height: 100dvh;

  @supports not (height: 100dvh) {
    min-height: 100vh;
  }
`;

const StyledLandingLoaderWrap = styled(motion.div)`
  min-height: 100dvh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Theme.colors.pageBgColor};

  @supports not (height: 100dvh) {
    min-height: 100vh;
  }
`;

const StyledLandingLoaderLogo = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const StyledProfileErrorWrap = styled(motion.div)`
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background:
    radial-gradient(
      circle at top center,
      rgba(106, 217, 255, 0.08) 0%,
      rgba(0, 0, 0, 0) 38%
    ),
    ${Theme.colors.pageBgColor};

  @supports not (height: 100dvh) {
    min-height: 100vh;
  }
`;

const StyledProfileErrorCard = styled.div`
  width: 100%;
  max-width: 430px;
  padding: 20px 18px;
  border-radius: 20px;
  border: 1px solid ${Theme.colors.borderColor};
  background:
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.03) 0%,
      rgba(255, 255, 255, 0.015) 100%
    ),
    linear-gradient(195deg, #111111 0%, ${Theme.colors.blockColor} 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 10px 24px rgba(0, 0, 0, 0.18);
  text-align: center;
`;

const StyledProfileErrorTitle = styled.h2`
  font-size: ${Theme.font.fontSize2};
  font-weight: ${Theme.font.fontWeight1};
  color: ${Theme.colors.mainFontColor};
`;

const StyledProfileErrorText = styled.p`
  margin-top: 8px;
  font-size: ${Theme.font.fontSize3};
  line-height: 1.5;
  font-weight: ${Theme.font.fontWeight3};
  color: ${Theme.colors.secondaryFontColor};
`;

const StyledRetryButton = styled.button`
  margin-top: 16px;
  width: 100%;
  min-height: 52px;
  border: none;
  outline: none;
  cursor: pointer;
  border-radius: 16px;
  color: #ffffff;
  font-size: ${Theme.font.fontSize3};
  font-weight: ${Theme.font.fontWeight2};

  background: linear-gradient(
    135deg,
    rgba(106, 217, 255, 0.92) 0%,
    ${Theme.colors.accentColor} 100%
  );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 10px 24px rgba(106, 217, 255, 0.18);

  transition:
    transform 0.16s ease,
    opacity 0.16s ease,
    box-shadow 0.16s ease;

  &:active {
    transform: scale(0.985);
  }

  &:disabled {
    cursor: default;
    opacity: 0.8;
  }
`;

const StyledRetryContent = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const StyledNotFound = styled.div`
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Theme.colors.mainFontColor};
  background: ${Theme.colors.pageBgColor};
  padding: 24px;
  text-align: center;

  @supports not (height: 100dvh) {
    min-height: 100vh;
  }
`;

const StyledAppRoot = styled.div`
  min-height: 100dvh;
  background: ${Theme.colors.pageBgColor};

  @supports not (height: 100dvh) {
    min-height: 100vh;
  }
`;
